import React, { useEffect, useState } from 'react';
import { formatWholeNumber } from '../../../../../core/utils/campaigns';
import { Avatar, Card, Col, Row, Select, Table, Tag } from 'antd';
import { PrimaryTableRowText, TableColumnTitle } from '../../../../shared/globalStyling/styledText';
import DonutChart from '../../../../shared/charts/DonutChart';
import { Html5Outlined, FileImageOutlined } from '@ant-design/icons';
import { formatCreativeName } from './utils';

const TopCreatives = ({ data = [], countKey = 'transactions', countLabel = 'Transactions' }) => {
  const [creatives, setCreatives] = useState(data);
  const [selectedTop, setSelectedTop] = useState('top5');
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (data) {
      let filteredData;
      switch (selectedTop) {
        case 'top5':
          filteredData = data.slice(0, 5);
          break;
        case 'top10':
          filteredData = data.slice(0, 10);
          break;
        case 'top20':
          filteredData = data.slice(0, 20);
          break;
        default:
          filteredData = data;
          break;
      }
      setCreatives(filteredData);

      // Calculate total
      if (filteredData.length > 0) {
        const total = filteredData.reduce((acc, curr) => acc + (curr[countKey] || 0), 0);
        setTotalCount(total);
      } else {
        setTotalCount(0);
      }
    }
  }, [countKey, data, selectedTop]);
  const renderAvatar = (record) => {
    const { url, color, name, isHtml5 } = record;
    const looksLikeImage = url && /\.(png|jpe?g|gif|avif|webp|bmp)(\?|$)/i.test(url);

    if (isHtml5) {
      return (
        <Avatar
          shape="square"
          size={50}
          icon={<Html5Outlined />}
          style={{ marginLeft: '-16px', backgroundColor: '#fa8c16', color: '#fff', fontSize: 24 }}
        />
      );
    }

    if (looksLikeImage) {
      return (
        <Avatar
          shape="square"
          size={50}
          src={url}
          icon={<FileImageOutlined />}
          style={{ marginLeft: '-16px', backgroundColor: color }}
        />
      );
    }

    // Numeric IDs (unmasked creatives) all start with the same digit — useless as initials.
    // Show a generic icon instead; keep initials only for real named creatives.
    const isNumericId = /^\d+$/.test(String(name));
    if (isNumericId) {
      return (
        <Avatar
          shape="square"
          size={50}
          icon={<FileImageOutlined />}
          style={{ marginLeft: '-16px', backgroundColor: color, color: '#fff' }}
        />
      );
    }

    const initial = String(name || '?').charAt(0).toUpperCase();
    return (
      <Avatar
        shape="square"
        size={50}
        style={{
          marginLeft: '-16px',
          backgroundColor: color,
          color: '#fff',
          fontWeight: 700,
          fontSize: 20,
        }}
      >
        {initial}
      </Avatar>
    );
  };

  const columns = [
    {
      title: '',
      key: 'avatar',
      width: '70px',
      align: 'left',
      render: (_, record) => renderAvatar(record),
    },
    {
      title: '',
      dataIndex: 'color',
      key: 'color',
      width: '15px',
      render: (text) => (
        <div
          style={{
            backgroundColor: text,
            height: '12px',
            width: '12px',
            minWidth: '12px',
            borderRadius: '50%',
            marginLeft: '-12px',
          }}
        />
      ),
    },
    {
      title: <TableColumnTitle text={'Ad Unit Name'} />,
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <PrimaryTableRowText text={formatCreativeName(text)} />
          <div style={{ marginTop: 4, display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {record.isHtml5 && (
              <Tag color="orange" style={{ fontSize: '11px', lineHeight: '18px', padding: '0 6px', margin: 0 }}>
                HTML5
              </Tag>
            )}
            {record.dsp && record.dsp !== 'Unknown' && (
              <Tag style={{ fontSize: '11px', lineHeight: '18px', padding: '0 6px', margin: 0 }}>
                {record.dsp === 'GoogleAdsManager' ? 'GAM' : record.dsp}
              </Tag>
            )}

          </div>
        </div>
      ),
    },
    {
      title: <TableColumnTitle text={'Impressions'} />,
      dataIndex: 'impressions',
      key: 'impressions',
      render: (text) => {
        return <PrimaryTableRowText text={formatWholeNumber(text)} />;
      },
    },
    {
      title: <TableColumnTitle text={countLabel} />,
      dataIndex: countKey,
      key: countKey,
      render: (text) => {
        return <PrimaryTableRowText text={formatWholeNumber(text)} />;
      },
    },
  ];

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} lg={18}>
        <Card
          title="Top Creatives"
          extra={
            <Select
              style={{ width: '100%' }}
              defaultValue={'top5'}
              options={[
                { label: 'Top 5', value: 'top5' },
                { label: 'Top 10', value: 'top10' },
                { label: 'Top 20', value: 'top20' },
                { label: 'All', value: 'all' },
              ]}
              value={selectedTop}
              onChange={(value) => {
                setSelectedTop(value);
              }}
            />
          }
        >
          <Table
            dataSource={creatives}
            columns={columns}
            pagination={{
              position: ['none', 'bottomCenter'],
              total: creatives.length,
              responsive: true,
              showSizeChanger: false,
              showLessItems: true,
              size: 'default',
            }}
            scroll={{ x: 200, y: 400 }}
            rowKey={({ name }) => name}
          />
        </Card>
      </Col>
      <Col xs={24} lg={6}>
        <Card title="Breakdown - Top Creatives" style={{ height: '100%' }}>
          <DonutChart
            axisName="name"
            position={countKey}
            chartData={creatives.map((c) => ({
              ...c,
              name: formatCreativeName(c.name),
            }))}
            showTooltip
            totalCount={totalCount}
            showCenterText={true}
            centerText={countLabel}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default TopCreatives;
